/* SALWAR MAHAL smart search (Oct 2026)
 *
 * 1. ROUTING — a search made only of words we understand goes to the matching filters:
 *      "saree"              -> Garment: Saree             (55 sarees instead of "1000 results")
 *      "velvet suit"        -> Fabric: Velvet             ("suit" is generic, so every velvet item shows)
 *      "chiffon unstitched" -> Fabric: Chiffon + Size: Unstitched Fabric
 *      "peach sharara"      -> title contains "peach" + Garment: Sharara Gharara
 *    The query keeps the shopper's words ("saree OR *"), so Shopify's search report still shows
 *    what people typed. Any word we don't know -> normal Shopify search, exactly as before.
 * 2. DROPDOWN — "Shop Sarees (55)" shortcuts above Shopify's suggestions, popular searches when
 *    the empty box is tapped, and rotating "Search for ..." hints in the header bar.
 *
 * Word list + counts live in the shop metafield custom.smart_search (built nightly by
 * theme_work/smart_search/build_search_data.py) and are fetched once via ?section_id=sm-search-data.
 */
(function () {
  if (window.SMSearch) return;

  let dataPromise = null;
  function loadData() {
    if (!dataPromise) {
      const key = 'sm_search_data_v1';
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (cached && cached.words) dataPromise = Promise.resolve(cached);
      } catch (e) {}
      if (!dataPromise) {
        dataPromise = fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + '?section_id=sm-search-data')
          .then((r) => r.text())
          .then((html) => {
            const m = html.match(/<script[^>]*id="sm-search-data"[^>]*>([\s\S]*?)<\/script>/);
            const d = m ? JSON.parse(m[1]) : null;
            if (d && d.words) {
              try { sessionStorage.setItem(key, JSON.stringify(d)); } catch (e) {}
            }
            return d;
          })
          .catch(() => null);
      }
    }
    return dataPromise;
  }

  const FILTER = { g: 'garment_type', c: 'colour_group', f: 'fabric_group', o: 'occasion', s: 'sizes_in_stock', d: 'delivery_speed' };

  function normalise(q) {
    return String(q || '')
      .toLowerCase()
      .replace(/[’'`]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Greedy longest match (up to 4 words). Returns {actions, unknown, words}
  function parse(q, data) {
    const toks = normalise(q).split(' ').filter(Boolean);
    const actions = [];
    const unknown = [];
    for (let i = 0; i < toks.length; ) {
      let hit = null;
      for (let n = Math.min(4, toks.length - i); n >= 1; n--) {
        const phrase = toks.slice(i, i + n).join(' ');
        if (data.words[phrase]) { hit = { phrase, n, acts: data.words[phrase].split('|') }; break; }
      }
      if (hit) { actions.push(...hit.acts); i += hit.n; }
      else { unknown.push(toks[i]); i += 1; }
    }
    return { actions, unknown, toks };
  }

  // Turn parsed actions into a destination URL (or null = leave to normal search)
  const STITCHED_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  function plan(q, data) {
    if (!data) return null;
    const p = parse(q, data);
    if (!p.toks.length || p.unknown.length) return null;
    const acts = p.actions.filter((a) => a !== 'x');
    if (!acts.length) return null;
    const coll = acts.filter((a) => a.startsWith('u:'));
    const ready = acts.includes('r');
    const rest = acts.filter((a) => a !== 'r' && !a.startsWith('u:'));
    if (coll.length) {
      // a collection word only works on its own ("sale", "winter", "chikankari")
      if (coll.length === 1 && !rest.length && !ready) return { url: coll[0].slice(2), label: data.ulabels[coll[0].slice(2)] || q, count: data.counts[coll[0]] };
      return null;
    }
    if (!rest.length) {
      return ready ? { url: data.ready, label: 'Ready to Wear', count: data.counts['u:' + data.ready] } : null;
    }
    // "stitched chiffon": stitched = any real size, so tick every size except "Unstitched Fabric"
    const params = new URLSearchParams();
    params.set('type', 'product');
    const titles = rest.filter((a) => a.startsWith('t:')).map((a) => 'title:' + a.slice(2));
    const words = normalise(q);
    params.set('q', titles.length ? titles.join(' ') : words + ' OR *');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'hide'); // sold-out items stay out of results
    const seen = new Set();
    rest.forEach((a) => {
      const k = FILTER[a[0]];
      if (!k || seen.has(a)) return;
      seen.add(a);
      params.append('filter.p.m.custom.' + k, a.slice(2));
    });
    if (ready && !rest.some((a) => a[0] === 's')) STITCHED_SIZES.forEach((v) => params.append('filter.p.m.custom.sizes_in_stock', v));
    params.set('sm', q.trim().slice(0, 80) || '1'); // what the shopper typed, shown back in the search bar
    const single = rest.length === 1 ? data.counts[rest[0]] : undefined;
    const text = label(rest, data) + (ready && !rest.some((a) => a[0] === 's') ? ' · Stitched' : '');
    return { url: '/search?' + params.toString(), label: text, count: ready ? undefined : single };
  }

  function nice(a, data) {
    const v = a.slice(2);
    if (a[0] === 't') return v.charAt(0).toUpperCase() + v.slice(1);
    return data.labels[v] || v;
  }
  function label(rest, data) {
    const order = ['t', 'c', 'f', 'o', 'g', 's', 'd'];
    const uniq = [...new Set(rest)];
    uniq.sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
    const g = uniq.filter((a) => a[0] === 'g');
    const parts = uniq.filter((a) => 'tcfo'.includes(a[0])).map((a) => nice(a, data));
    let text = parts.join(' ');
    if (g.length) text = (text ? text + ' ' : '') + g.map((a) => nice(a, data)).join(' / ');
    const tail = uniq.filter((a) => 'sd'.includes(a[0])).map((a) => nice(a, data));
    if (tail.length) text = text ? text + ' · ' + tail.join(' · ') : tail.join(' · ');
    return text;
  }

  // Suggestions for what has been typed so far: complete the LAST word against the word list.
  function suggest(q, data, max) {
    const norm = normalise(q);
    if (!norm || !data) return [];
    const out = [];
    const seen = new Set();
    // same filters reached by different spellings ("saree", "sarees", "sari") = one shortcut
    const push = (s) => {
      const key = s && s.url.replace(/([?&])(q|sm)=[^&]*/g, '$1'); // ignore the typed word + its tracking tag
      if (s && !seen.has(key)) { seen.add(key); out.push(s); }
    };
    const toks = norm.split(' ');
    const last = toks.pop();
    const head = toks.join(' ');
    // exact phrase first
    push(plan(norm, data));
    if (last.length >= 2) {
      const cands = Object.keys(data.words)
        .filter((w) => w.startsWith(last) && w !== last && data.words[w] !== 'x')
        .sort((a, b) => (data.counts[data.words[b]] || 0) - (data.counts[data.words[a]] || 0) || a.length - b.length);
      for (const w of cands) {
        push(plan((head ? head + ' ' : '') + w, data));
        if (out.length >= max) break;
      }
    }
    return out.slice(0, max);
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  function shortcutsHTML(list, heading) {
    if (!list.length) return '';
    return (
      '<div class="sm-ps-shortcuts"><h2 class="predictive-search__heading text-body caption-with-letter-spacing">' +
      esc(heading) +
      '</h2><ul class="predictive-search__results-list list-unstyled" role="group">' +
      list
        .map(
          (s, i) =>
            '<li id="sm-ps-shortcut-' + i + '" class="predictive-search__list-item" role="option" aria-selected="false">' +
            '<a href="' + esc(s.url) + '" class="predictive-search__item link link--text sm-ps-shortcut" tabindex="-1">' +
            '<span class="sm-ps-shortcut__text">Shop <strong>' + esc(s.label) + '</strong></span>' +
            (s.count ? '<span class="sm-ps-shortcut__count">' + s.count + '</span>' : '') +
            '<svg class="sm-ps-shortcut__arrow" viewBox="0 0 14 10" aria-hidden="true"><path d="M8.5 1l4 4-4 4M12.5 5H1" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</a></li>'
        )
        .join('') +
      '</ul></div>'
    );
  }

  function popularHTML(data) {
    const chips = (data.popular || [])
      .map((w) => {
        const p = plan(w, data);
        const href = p ? p.url : '/search?type=product&options%5Bunavailable_products%5D=hide&q=' + encodeURIComponent(w);
        return '<li class="predictive-search__list-item" role="option" aria-selected="false"><a class="sm-ps-chip" tabindex="-1" href="' + esc(href) + '">' + esc(w) + '</a></li>';
      })
      .join('');
    return (
      '<div id="predictive-search-results" class="sm-ps-popular" role="listbox">' +
      '<h2 class="predictive-search__heading text-body caption-with-letter-spacing">Popular searches</h2>' +
      '<ul class="sm-ps-chips list-unstyled" role="group">' + chips + '</ul>' +
      '<span hidden data-predictive-search-live-region-count-value>Popular searches</span></div>'
    );
  }

  // ── hook into Dawn's <predictive-search> ──
  function enhance() {
    const PS = customElements.get('predictive-search');
    if (!PS || PS.prototype.__smEnhanced) return;
    PS.prototype.__smEnhanced = true;

    const render = PS.prototype.renderSearchResults;
    PS.prototype.renderSearchResults = function (markup) {
      render.call(this, markup);
      const term = this.getQuery();
      loadData().then((data) => {
        if (!data || this.getQuery() !== term) return;
        const list = suggest(term, data, 4);
        if (!list.length) return;
        const host = this.querySelector('#predictive-search-results');
        if (!host || host.querySelector('.sm-ps-shortcuts')) return;
        host.insertAdjacentHTML('afterbegin', shortcutsHTML(list, 'Shop by'));
      });
    };

    // Dawn binds its focus/keyup handlers when each search box is created (before this file
    // loads), so popular searches hook in through focusin + the shared onChange instead.
    PS.prototype.smShowPopular = function () {
      loadData().then((data) => {
        if (!data || this.getQuery().length || document.activeElement !== this.input) return;
        this.predictiveSearchResults.innerHTML = popularHTML(data);
        this.setAttribute('results', true);
        this.open();
      });
    };
    document.addEventListener('focusin', (event) => {
      const el = event.target && event.target.closest && event.target.closest('predictive-search');
      if (el && event.target === el.input && !el.getQuery().length) el.smShowPopular();
    });

    // Dawn closes the dropdown when the box is emptied; show popular searches instead
    const onChange = PS.prototype.onChange;
    PS.prototype.onChange = function () {
      onChange.call(this);
      if (!this.getQuery().length && document.activeElement === this.input) this.smShowPopular();
    };
  }

  // ── send whole-form searches to the filters ──
  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/search\/?$/.test(action.split('?')[0])) return;
    if (form.querySelector('[aria-selected="true"] a')) return; // a dropdown item is chosen: Dawn opens it
    const input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;
    if (form.dataset.smRouting === '1') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.dataset.smRouting = '1';
    const q = input.value;
    const fallback = () => {
      form.dataset.smRouting = '';
      form.submit();
    };
    Promise.race([loadData(), new Promise((r) => setTimeout(() => r(null), 1500))]).then((data) => {
      const p = plan(q, data);
      if (p) window.location.href = p.url;
      else fallback();
    });
  }

  // ── /search?q=saree opened directly (old links, browser history): route once ──
  function routeSearchPage() {
    if (!/^\/search\/?$/.test(location.pathname)) return;
    const params = new URLSearchParams(location.search);
    // Shopify's own suggestion links and old bookmarks come without "hide sold out": add it once
    if (params.get('q') && !params.get('options[unavailable_products]')) {
      params.set('options[unavailable_products]', 'hide');
      location.replace(location.pathname + '?' + params.toString());
      return;
    }
    const q = params.get('q');
    if (!q || params.get('sm') || [...params.keys()].some((k) => k.startsWith('filter.') || k === 'sort_by' || k === 'page')) return;
    loadData().then((data) => {
      const p = plan(q, data);
      if (p) location.replace(p.url);
    });
  }

  // ── results page: keep the shopper's words in the search bar ──
  function showTypedTerms() {
    if (!/^\/search\/?$/.test(location.pathname)) return;
    const params = new URLSearchParams(location.search);
    const sm = params.get('sm');
    const typed = sm && sm !== '1' ? sm : (params.get('q') || '').replace(/ OR \*$/, '').replace(/title:/g, '').replace(/^\*$/, '');
    if (!typed) return;
    document.querySelectorAll('.sm-searchbar input[type="search"]').forEach((el) => {
      if (!el.value) el.value = typed;
      const reset = el.form && el.form.querySelector('.reset__button');
      if (reset) reset.classList.remove('hidden');
    });
  }

  // ── rotating "Search for ..." hints in the header bar ──
  function rotateHints() {
    const inputs = document.querySelectorAll('.sm-searchbar input[type="search"]');
    if (!inputs.length) return;
    loadData().then((data) => {
      const hints = (data && data.hints) || [];
      if (!hints.length) return;
      let i = 0;
      const set = () => inputs.forEach((el) => {
        if (document.activeElement !== el && !el.value) el.setAttribute('placeholder', 'Search for "' + hints[i % hints.length] + '"');
      });
      set();
      setInterval(() => { i++; set(); }, 3500);
    });
  }

  window.SMSearch = { loadData, parse, plan, suggest };
  document.addEventListener('submit', onSubmit, true);
  routeSearchPage();
  const start = () => {
    enhance();
    showTypedTerms();
    rotateHints();
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    idle(() => loadData());
  };
  if (customElements.get('predictive-search')) start();
  else customElements.whenDefined('predictive-search').then(start);
})();
